import {
  PROTOCOL_VERSION,
  assertNever,
  createRoomResponseSchema,
  type CreateRoomErrorCode,
  type RoomType,
  type RoomVisibility,
} from '@2d-space/contracts';

import { buildInviteShareUrl, type InviteHttpRequest } from './invite-join.js';

export interface CreateRoomFailure {
  readonly code: CreateRoomErrorCode;
  readonly message: string;
  readonly canRetry: boolean;
  readonly retryAfterSeconds: number | undefined;
}

export type CreateRoomOutcome =
  | {
      readonly ok: true;
      readonly slug: string;
      readonly token: string;
      readonly path: string;
      readonly browserCredential: string;
      readonly ownerCapability: string;
      readonly privateAccessCode?: string;
    }
  | { readonly ok: false; readonly failure: CreateRoomFailure };

export function createRoomFailure(
  code: CreateRoomErrorCode,
  retryAfterSeconds?: number,
): CreateRoomFailure {
  switch (code) {
    case 'disabled':
      return {
        code,
        message: 'This server does not offer room creation. Ask the organiser for an invite link.',
        canRetry: false,
        retryAfterSeconds: undefined,
      };
    case 'rate-limited':
      return {
        code,
        message:
          retryAfterSeconds === undefined
            ? 'You have created several rooms just now. Wait a moment and try again.'
            : `You have created several rooms just now. Try again in ${retryAfterSeconds}s.`,
        canRetry: true,
        retryAfterSeconds,
      };
    case 'capacity-reached':
      return {
        code,
        message: 'The server is already running as many rooms as it allows. Try again shortly.',
        canRetry: true,
        retryAfterSeconds,
      };
    case 'invalid-request':
    case 'internal-error':
      return {
        code,
        message: 'The room could not be created. Try again.',
        canRetry: true,
        retryAfterSeconds: undefined,
      };
    default:
      return assertNever(code);
  }
}

function readRetryAfterSeconds(value: string | null): number | undefined {
  if (value === null || !/^\d+$/.test(value)) {
    return undefined;
  }
  const seconds = Number(value);
  return Number.isSafeInteger(seconds) && seconds > 0 ? seconds : undefined;
}

/**
 * Asks the server for a new room and returns the in-app path that enters it.
 *
 * A path rather than an absolute URL, because the caller navigates with the
 * router. `buildInviteShareUrl` produces the absolute form for sharing, and
 * both are derived from the same function so the `?t=` parameter cannot drift
 * between the two.
 */
export async function createRoom(
  serverUrl: string,
  inputOrRequest:
    | { readonly title: string; readonly roomType: RoomType; readonly visibility: RoomVisibility }
    | InviteHttpRequest,
  requestOverride?: InviteHttpRequest,
): Promise<CreateRoomOutcome> {
  const input =
    typeof inputOrRequest === 'function'
      ? { title: 'Untitled room', roomType: 'instant' as const, visibility: 'public' as const }
      : inputOrRequest;
  const request =
    typeof inputOrRequest === 'function' ? inputOrRequest : (requestOverride ?? globalThis.fetch);
  let response;
  let body: unknown;
  try {
    response = await request(new URL('/rooms', serverUrl).toString(), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ version: PROTOCOL_VERSION, ...input }),
    });
    // Inside the try, not after it. A reverse proxy answering with an HTML
    // error page, or an empty body, makes `json()` reject — and a rejection
    // here escapes to the caller instead of returning a failure, leaving the
    // button stuck on "Creating room…" with nothing said. That is the realistic
    // shape of an upstream error, not an exotic one.
    body = await response.json();
  } catch {
    return { ok: false, failure: createRoomFailure('internal-error') };
  }

  const parsed = createRoomResponseSchema.safeParse(body);
  if (!parsed.success) {
    // An unrecognised shape is not a room. Treating it as one would send the
    // browser to a route with an undefined token and a misleading error.
    return { ok: false, failure: createRoomFailure('internal-error') };
  }
  if (!parsed.data.ok) {
    return {
      ok: false,
      failure: createRoomFailure(
        parsed.data.error.code,
        parsed.data.error.retryAfterSeconds ??
          readRetryAfterSeconds(response.headers.get('Retry-After')),
      ),
    };
  }

  const { slug, token } = parsed.data;
  const shareUrl = new URL(buildInviteShareUrl('http://placeholder.invalid', slug, token));
  return {
    ok: true,
    slug,
    token,
    path: `${shareUrl.pathname}${shareUrl.search}`,
    browserCredential: parsed.data.browserCredential,
    ownerCapability: parsed.data.ownerCapability,
    ...(parsed.data.privateAccessCode === undefined
      ? {}
      : { privateAccessCode: parsed.data.privateAccessCode }),
  };
}
